import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, useTheme, useMediaQuery } from "@mui/material";
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import { EventClickArg, EventInput } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from '@fullcalendar/multimonth';
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import { tokens } from "../../theme";
import { api, apiEvents, calendarBaseRoute, IEvent } from '../../types';
import { createApiHeaders } from '../../utilities/JwtTokenParser';
import Header from "../../components/Header";
import Loader from "../../components/Loader";

interface CalendarListProps {
  currentPharmacy: null | string;
}

const fetchEventList = async (year: number): Promise<IEvent[]> => {
  const data = await fetch(`${api}${apiEvents}year/${year}`, {
    method: 'GET',
    headers: createApiHeaders(true, true)
  });

  const json = await data.json();
  return json;
};

const mapToCalendarEvents = (events?: IEvent[]): EventInput[] => {
  if (!events) {
    return [];
  }

  const calendarEvents: EventInput[] = []; 
  events.forEach(ev => calendarEvents.push({
    id: `${ev.id}`, 
    title: ev.subject,
    start: ev.startDateTime,
    end: ev.endDateTime,
    extendedProps: {
      location: ev.location,
      description: ev.description
    }
  })); 

  return calendarEvents;
};

const CalendarList = ({ currentPharmacy }: CalendarListProps) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState<EventInput[]>([]);

  const refresh = (year: number) => {
    setIsLoading(true); 
    fetchEventList(year)
      .then((response) => { 
        const typedResponse = response as IEvent[];
        const mappedEvents = mapToCalendarEvents(typedResponse);
        setCalendarEvents(mappedEvents); 
        setIsLoading(false);
        setIsSuccess(true);
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
        setIsSuccess(false);
      });
  };

  useEffect(() => {
    if (currentPharmacy) {
      refresh((new Date()).getFullYear());
    }
  }, [currentPharmacy]);

  const handleEventClick = (selected: EventClickArg) => {
    const clickedEvent: IEvent = {
      id: parseInt(selected.event.id),
      subject: selected.event.title, 
      location: selected.event.extendedProps.location, 
      description: selected.event.extendedProps.description,
      startDateTime: selected.event.start as Date, 
      endDateTime: selected.event.end as Date
    };

    navigate(`${calendarBaseRoute}/${clickedEvent.id}`, { state: clickedEvent });
  };

  return (
    <Box m="14px">
      <Box 
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap="20px" 
        sx={{
          "& > div": { gridColumn: isNonMobile ? undefined : "span 12" }
        }}
      >
        <Box 
          display="flex"
          sx={{
            gridColumn: 'span 4'
          }}
        >
          <Header title="CALENDAR" subtitle="MANAGE EVENTS AND APPOINTMENTS" />
        </Box>

        <Box
          display="flex" 
          justifyContent="center"
          sx={{
            gridColumn: 'span 4'
          }}
        >
          <Loader isLoading={isLoading} isSuccess={isSuccess} />
        </Box>

        <Box
          display="flex" 
          justifyContent="end"
          sx={{
            gridColumn: 'span 4'
          }}
        >
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "8px 14px",
              margin: "30px 0px"
            }}
            onClick={() => refresh((new Date()).getFullYear())}
          >
            <RefreshIcon sx={{ mr: "8px" }} />
            Refresh
          </Button>
        </Box>
      </Box>

      <Box 
        flex="1 1 100%"
        m="10px 0 0 0"
      >
        <FullCalendar
          height="75vh" 
          customButtons={{
            createEventButton: {
              text: "+ ADD EVENT",
              click: () => {
                navigate(`${calendarBaseRoute}/0`);
              }
            }
          }}
          plugins={[
            timeGridPlugin, 
            dayGridPlugin, 
            multiMonthPlugin,
            interactionPlugin, 
            listPlugin
          ]}
          headerToolbar={{
            left: "prev,next today createEventButton",
            center: "title",
            right: "timeGridDay,timeGridWeek,dayGridMonth,listMonth,multiMonthYear"
          }}
          buttonText={{
            today: "TODAY",
            day: "DAY", 
            week: "WEEK",
            month: "MONTH",
            list: "LIST",
            year: "YEAR"
          }}
          initialView="timeGridWeek"
          editable={true}
          selectable={true}
          selectMirror={false}
          dayMaxEvents={true}
          navLinks={true}
          nowIndicator={true}
          eventClick={handleEventClick}
          events={calendarEvents}
        />
      </Box>
    </Box>
  );
};

export default CalendarList;